import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatPositionsDisplay } from '../../../../services/positionAPIs';
import { useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';
import { alpha } from '@mui/material/styles';

import MainCard from '../../../../components/cards/MainCard';
import Transitions from '../../../../components/extended/Transitions';
import useConfig from '../../../../hooks/useConfig';

import User1 from '../../../../assets/images/user/profile.png';
import { IconLogout, IconSettings, IconBell, IconBellOff } from '@tabler/icons-react';

import { getMyProfile } from '../../../../services/EmployeeService';
import { getEmployeeImage } from '../../../../services/HRstaff';

export default function ProfileSection({ notification, setNotification }) {
  const theme = useTheme();
  const { borderRadius } = useConfig();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [userName, setUserName] = useState('Loading...');
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [userPosition, setUserPosition] = useState('Project Admin');
  const [profile, setProfile] = useState(null);

  const anchorRef = useRef(null);

  const handleToggle = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }
    prevOpen.current = open;
  }, [open]);

  // Fetch user data
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const employee = await getMyProfile();
        setProfile(employee);
        setUserName(`${employee.firstName} ${employee.lastName}`);
        setUserPosition(formatPositionsDisplay(employee) || 'Project Admin');

        if (employee.image) {
          try {
            const employeeId = localStorage.getItem('employeeId') || employee.employeeId;
            const imageUrl = await getEmployeeImage(employeeId);
            setProfileImageUrl(imageUrl);
          } catch (imageError) {
            console.warn('Failed to load profile image:', imageError);
            setProfileImageUrl(null);
          }
        }
      } catch (error) {
        console.error('Error fetching profile data:', error);
        setUserName('User');
        setUserPosition('Project Admin');
      }
    };

    fetchProfile();

    return () => {
      if (profileImageUrl) {
        URL.revokeObjectURL(profileImageUrl);
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (profileImageUrl) {
        URL.revokeObjectURL(profileImageUrl);
      }
    };
  }, [profileImageUrl]);

  const goToProfile = () => {
    navigate('/profile');
    setOpen(false);
  };

  // New function to handle dashboard redirection
  const handleDashboardRedirect = () => {
    navigate('/Mydashboard');
    setOpen(false);
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const generateInitials = () => {
    if (profile?.firstName && profile?.lastName) {
      return `${profile.firstName.charAt(0)}${profile.lastName.charAt(0)}`;
    }
    return 'U';
  };

  return (
    <>
      <Chip
        sx={{
          ml: 2,
          height: '48px',
          alignItems: 'center',
          borderRadius: '27px',
          transition: 'all 0.3s ease',
          background: open 
            ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.light, 0.1)})`
            : theme.palette.background.paper,
          border: `1px solid ${alpha(theme.palette.primary.main, open ? 0.3 : 0.1)}`,
          '&:hover': {
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.primary.light, 0.15)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
            transform: 'translateY(-2px)',
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
          },
          '& .MuiChip-label': {
            lineHeight: 0
          }
        }}
        icon={
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            variant="dot"
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: notification ? '#44b700' : theme.palette.grey[400],
                color: notification ? '#44b700' : theme.palette.grey[400],
                boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
                width: 10,
                height: 10,
                borderRadius: '50%',
                '&::after': {
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  animation: notification ? 'ripple 1.2s infinite ease-in-out' : 'none',
                  border: '1px solid currentColor',
                  content: '""'
                }
              },
              '@keyframes ripple': {
                '0%': {
                  transform: 'scale(.8)',
                  opacity: 1
                },
                '100%': {
                  transform: 'scale(2.4)',
                  opacity: 0
                }
              }
            }}
          >
            <Avatar
              src={profileImageUrl || User1}
              alt="user-images"
              sx={{
                ...theme.typography.mediumAvatar,
                margin: '8px 0 8px 8px !important',
                cursor: 'pointer',
                border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                  border: `2px solid ${theme.palette.primary.main}`
                }
              }}
              ref={anchorRef}
              aria-controls={open ? 'menu-list-grow' : undefined}
              aria-haspopup="true"
              color="inherit"
              onError={() => setProfileImageUrl(null)}
            >
              {!profileImageUrl && generateInitials()}
            </Avatar>
          </Badge>
        }
        label={
          <IconSettings 
            stroke={1.5} 
            size="24px" 
            style={{ 
              transition: 'transform 0.3s ease',
              transform: open ? 'rotate(90deg)' : 'rotate(0deg)'
            }} 
          />
        }
        onClick={handleToggle}
        color="primary"
        aria-label="user-account"
      />
      <Popper
        placement="bottom"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        modifiers={[{ name: 'offset', options: { offset: [0, 14] } }]}
        sx={{ maxWidth: '340px', mr: 1, zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions in={open} {...TransitionProps}>
              <Paper
                elevation={8}
                sx={{
                  background: theme.palette.background.paper,
                  borderRadius: 3,
                  overflow: 'hidden',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                }}
              >
                {open && (
                  <MainCard 
                    border={false} 
                    elevation={0} 
                    content={false} 
                    boxShadow={false}
                    sx={{ background: theme.palette.background.paper }}
                  >
                    {/* Header Section with Gradient Background */}
                    <Box
                      sx={{
                        p: 3,
                        pb: 2,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.light, 0.05)})`,
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '3px',
                          background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`
                        }
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        {/* THIS IS THE MENU PROFILE IMAGE */}
                        <Avatar
                          src={profileImageUrl || User1}
                          alt="user-profile"
                          onClick={handleDashboardRedirect} // CLICK HANDLER ADDED HERE
                          sx={{
                            width: 56,
                            height: 56,
                            border: `3px solid ${theme.palette.background.paper}`,
                            boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.2)}`,
                            cursor: 'pointer', // SHOW POINTER CURSOR
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                                transform: 'scale(1.05)', // SCALE EFFECT ON HOVER
                                boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`
                            }
                          }}
                          onError={() => setProfileImageUrl(null)}
                        >
                          {!profileImageUrl && generateInitials()}
                        </Avatar>
                        <Stack spacing={0.5} sx={{ flex: 1 }}>
                          <Typography 
                            variant="h5" 
                            sx={{ 
                              fontWeight: 600,
                              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              backgroundClip: 'text'
                            }}
                          >
                            {userName}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: theme.palette.text.secondary,
                              fontWeight: 500
                            }}
                          >
                            {userPosition}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Box>

                    <Box sx={{ px: 2, py: 1.5 }}>
                      {/* Notification Card */}
                      <Card
                        sx={{
                          bgcolor: notification 
                            ? alpha(theme.palette.primary.main, 0.08)
                            : alpha(theme.palette.grey[500], 0.05),
                          border: `1px solid ${notification 
                            ? alpha(theme.palette.primary.main, 0.2)
                            : alpha(theme.palette.grey[500], 0.1)}`,
                          borderRadius: 2,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                            transform: 'translateY(-2px)'
                          }
                        }}
                      >
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Grid container alignItems="center" justifyContent="space-between">
                            <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              {notification ? (
                                <IconBell size={20} color={theme.palette.primary.main} />
                              ) : (
                                <IconBellOff size={20} color={theme.palette.text.secondary} />
                              )}
                              <Stack>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  Notifications
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {notification ? 'Enabled' : 'Disabled'}
                                </Typography>
                              </Stack>
                            </Grid>
                            <Grid item>
                              <Switch
                                checked={notification}
                                onChange={(e) => setNotification(e.target.checked)}
                                name="notification"
                                sx={{
                                  '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: theme.palette.primary.main,
                                    '&:hover': {
                                      backgroundColor: alpha(theme.palette.primary.main, 0.08)
                                    }
                                  },
                                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: theme.palette.primary.main
                                  }
                                }}
                              />
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>

                      <Divider sx={{ my: 1.5, opacity: 0.6 }} />

                      {/* Action List */}
                      <List
                        component="nav"
                        sx={{
                          width: '100%',
                          p: 0,
                          '& .MuiListItemButton-root': {
                            borderRadius: 2,
                            mb: 0.5,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.08),
                              transform: 'translateX(4px)',
                              '& .MuiListItemIcon-root': {
                                color: theme.palette.primary.main
                              }
                            }
                          }
                        }}
                      >
                        <ListItemButton onClick={goToProfile}>
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <IconSettings stroke={1.5} size="20px" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                Security Settings
                              </Typography>
                            }
                          />
                        </ListItemButton>
                        
                        <ListItemButton 
                          onClick={handleLogout}
                          sx={{
                            '&:hover': {
                              backgroundColor: `${alpha(theme.palette.error.main, 0.08)} !important`,
                              '& .MuiListItemIcon-root': {
                                color: `${theme.palette.error.main} !important`
                              },
                              '& .MuiTypography-root': {
                                color: theme.palette.error.main
                              }
                            }
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <IconLogout stroke={1.5} size="20px" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                Logout
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </List>
                    </Box>
                  </MainCard>
                )}
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
}